import React, { useState } from "react";
import { useLanguage } from "@/App";
import { Check, ArrowRight, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGE_MAP = ["starter", "growth", "premium", null]; // null = custom

const Pricing = () => {
  const { t, language } = useLanguage();
  const packages = t("pricing.packages");
  const [loadingPkg, setLoadingPkg] = useState(null);

  const handleCheckout = async (packageId) => {
    if (!packageId) return;
    setLoadingPkg(packageId);

    try {
      const response = await fetch(`${API}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error("Checkout failed");
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(language === "nl" ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Please try again.");
    } finally {
      setLoadingPkg(null);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 max-w-2xl">
          <p
            data-testid="pricing-overline"
            className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4"
          >
            {t("pricing.overline")}
          </p>
          <h2
            data-testid="pricing-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("pricing.title")}
          </h2>
          <p className="text-zinc-400 text-lg">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {packages.map((pkg, index) => {
            const isPopular = index === 1;
            const isCustom = index === 3;
            const packageId = PACKAGE_MAP[index];

            return (
              <div
                key={index}
                data-testid={`pricing-package-${pkg.name.toLowerCase()}`}
                className={`relative bg-[#0A0A0A] p-8 md:p-10 flex flex-col ${
                  isPopular ? "border border-[#FF6B00] shadow-[0_0_30px_rgba(255,59,48,0.15)]" : ""
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div
                    data-testid="pricing-popular-badge"
                    className="absolute -top-px left-1/2 -translate-x-1/2 bg-[#FF6B00] text-white text-xs font-bold uppercase tracking-wider px-4 py-1"
                  >
                    {t("pricing.popular")}
                  </div>
                )}

                {/* Package Name */}
                <h3
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {pkg.name}
                </h3>

                {/* Price */}
                <div className="mb-4">
                  <span
                    className={`text-4xl md:text-5xl font-black ${
                      isPopular ? "text-[#FF6B00]" : "text-white"
                    }`}
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {pkg.price}
                  </span>
                  {!isCustom && (
                    <span className="text-zinc-500 text-sm ml-2">{language === "nl" ? "eenmalig" : "one-time"}</span>
                  )}
                </div>

                {/* Description */}
                <p className="text-zinc-400 text-sm mb-6">
                  {pkg.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, fIndex) => (
                    <li
                      key={fIndex}
                      className="flex items-start gap-3 text-sm text-zinc-300"
                    >
                      <Check
                        size={16}
                        className={`mt-0.5 flex-shrink-0 ${
                          isPopular ? "text-[#FF6B00]" : "text-zinc-500"
                        }`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Buttons */}
                <div className="space-y-2">
                  {/* Buy Now / Stripe button */}
                  {!isCustom && (
                    <button
                      onClick={() => handleCheckout(packageId)}
                      disabled={loadingPkg === packageId}
                      data-testid={`pricing-buy-${pkg.name.toLowerCase()}`}
                      className={`w-full group inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold tracking-wide transition-all ${
                        isPopular
                          ? "bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90 hover:shadow-[0_0_30px_rgba(255,107,0,0.3)]"
                          : "bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20"
                      } disabled:opacity-50`}
                    >
                      {loadingPkg === packageId ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          {language === "nl" ? "Laden..." : "Loading..."}
                        </>
                      ) : (
                        <>
                          <CreditCard size={16} />
                          {language === "nl" ? "Direct Bestellen" : "Buy Now"}
                        </>
                      )}
                    </button>
                  )}

                  {/* Contact / Custom button */}
                  <a
                    href="#contact"
                    data-testid={`pricing-cta-${pkg.name.toLowerCase()}`}
                    className={`w-full group inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold tracking-wide transition-all ${
                      isCustom
                        ? "bg-[#FF6B00] text-white hover:bg-[#FF6B00]/90"
                        : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {isCustom
                      ? (language === "nl" ? "Neem Contact Op" : "Get In Touch")
                      : (language === "nl" ? "Eerst Overleggen" : "Talk First")
                    }
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Security badge */}
        <div className="mt-8 flex items-center justify-center gap-3 text-zinc-500">
          <ShieldCheck size={18} className="text-green-500" />
          <span className="text-sm">
            {language === "nl"
              ? "Veilig betalen via Stripe - SSL versleuteld"
              : "Secure payments via Stripe - SSL encrypted"}
          </span>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
